import { useState } from "react";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import sash1 from "@/assets/sash-template-1.png";
import sash2 from "@/assets/sash-template-2.png";
import sash3 from "@/assets/sash-template-3.png";
import sash4 from "@/assets/sash-template-4.png";
import sash5 from "@/assets/sash-template-5.png";
import sash6 from "@/assets/sash-template-6.png";
import sash7 from "@/assets/sash-template-7.png";
import sash8 from "@/assets/sash-template-8.png";
import sash9 from "@/assets/sash-template-9.png";
import sash10 from "@/assets/sash-template-10.png";
import sash11 from "@/assets/sash-template-11.png";
import sash12 from "@/assets/sash-template-12.png";
import sash13 from "@/assets/sash-template-13.png";
import sash14 from "@/assets/sash-template-14.png";
import sash15 from "@/assets/sash-template-15.png";
import sash16 from "@/assets/sash-template-16.png";
import sash17 from "@/assets/sash-template-17.png";
import sash18 from "@/assets/sash-template-18.png";
import sash19 from "@/assets/sash-template-19.png";
import { Check } from "lucide-react";

interface PatternSelectorProps {
  selectedPattern: string;
  onSelectPattern: (pattern: string) => void;
}

const patterns = [
  { id: "sash-1", name: "Classic Rasta", image: sash1 },
  { id: "sash-2", name: "Royal Blue", image: sash2 },
  { id: "sash-3", name: "Bold Red", image: sash3 },
  { id: "sash-4", name: "Pattern 4", image: sash4 },
  { id: "sash-5", name: "Pattern 5", image: sash5 },
  { id: "sash-6", name: "Pattern 6", image: sash6 },
  { id: "sash-7", name: "Pattern 7", image: sash7 },
  { id: "sash-8", name: "Pattern 8", image: sash8 },
  { id: "sash-9", name: "Pattern 9", image: sash9 },
  { id: "sash-10", name: "Pattern 10", image: sash10 },
  { id: "sash-11", name: "Pattern 11", image: sash11 },
  { id: "sash-12", name: "Pattern 12", image: sash12 },
  { id: "sash-13", name: "Pattern 13", image: sash13 },
  { id: "sash-14", name: "Pattern 14", image: sash14 },
  { id: "sash-15", name: "Pattern 15", image: sash15 },
  { id: "sash-16", name: "Pattern 16", image: sash16 },
  { id: "sash-17", name: "Pattern 17", image: sash17 },
  { id: "sash-18", name: "Pattern 18", image: sash18 },
  { id: "sash-19", name: "Pattern 19", image: sash19 },
];

export const PatternSelector = ({
  selectedPattern,
  onSelectPattern,
}: PatternSelectorProps) => {
  const [selectedTemplateType, setSelectedTemplateType] = useState("sash");

  const templateTypes = [
    { id: "sash", name: "Sash Template" },
    { id: "bag", name: "Bag Template" },
    { id: "smock", name: "Smock Template" },
    { id: "kente-tie", name: "Kente Tie" },
  ];

  return (
    <div className="space-y-4">
      {/* Template Type Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Template Type</label>
        <Select
          value={selectedTemplateType}
          onValueChange={setSelectedTemplateType}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select template type" />
          </SelectTrigger>
          <SelectContent>
            {templateTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pattern Grid - Only show for Sash Template */}
      {selectedTemplateType === "sash" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {patterns.map((pattern) => (
            <button
              key={pattern.id}
              onClick={() => onSelectPattern(pattern.id)}
              className="relative group focus:outline-none touch-manipulation"
            >
              <Card
                className={`overflow-hidden transition-all ${
                  selectedPattern === pattern.id
                    ? "ring-2 ring-primary shadow-[var(--shadow-glow)]"
                    : "hover:ring-2 hover:ring-primary/50 active:ring-2 active:ring-primary/50"
                }`}
              >
                <div className="aspect-square relative">
                  <img
                    src={pattern.image}
                    alt={pattern.name}
                    className="w-full h-full object-cover"
                  />
                  {selectedPattern === pattern.id && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <div className="bg-primary text-primary-foreground rounded-full p-1">
                        <Check className="h-4 w-4" />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              <p className="text-xs text-center mt-1 font-medium truncate">
                {pattern.name}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Placeholder for other template types */}
      {selectedTemplateType !== "sash" && (
        <div className="flex items-center justify-center h-32 bg-muted/50 rounded-lg">
          <p className="text-muted-foreground text-sm">
            {templateTypes.find((t) => t.id === selectedTemplateType)?.name}{" "}
            templates coming soon!
          </p>
        </div>
      )}
    </div>
  );
};
